import { useEffect, useState } from "react";
import { Layout } from "./components/layout/Layout";
import { StatsCard } from "./components/StatsCard";
import { supabaseReservation } from "../../..//integrations/supabase/client";
import { Calendar, CalendarCheck, CalendarDays, List, Grid3X3, Plus, Pencil, X } from "lucide-react";
import { format, isToday, isThisWeek, isSameDay } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Badge } from "../../ui/badge";
import { Calendar as CalendarComponent } from "./components/ui/calendar";
import { Button } from "../../ui/button";
import { cn } from "../../lib/utils";
import { ReservationDialog } from "./components/reservations/ReservationDialog";
import { CancelReservationDialog } from "./components/reservations/CancelReservationDialog";

interface Reservation {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  date: string;
  time: string;
  guests: number;
  status: string;
  outlet_id: string | null;
  created_at: string;
}

interface BlockedDate {
  id: string;
  block_date: string;
  block_time: string | null;
  reason: string | null;
  is_full_day: boolean;
}

const Reservations = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'table' | 'calendar'>('table');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);

  useEffect(() => {
    fetchReservations();
    fetchBlockedDates();

    // Set up realtime subscription
    const channel = supabaseReservation
      .channel('reservations-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'reservations' },
        () => {
          fetchReservations();
        }
      )
      .subscribe();

    return () => {
      supabaseReservation.removeChannel(channel);
    };
  }, []);

  const fetchReservations = async () => {
    const { data, error } = await supabaseReservation
      .from('reservations')
      .select('*')
      .order('date', { ascending: true })
      .order('time', { ascending: true });

    if (error) {
      console.error('Error fetching reservations:', error);
    } else {
      setReservations(data || []);
    }
    setLoading(false);
  };

  const fetchBlockedDates = async () => {
    const { data, error } = await supabaseReservation
      .from('blocked_dates')
      .select('*');

    if (error) {
      console.error('Error fetching blocked dates:', error);
    } else {
      setBlockedDates(data || []);
    }
  };

  const totalReservations = reservations.length;
  const todaysReservations = reservations.filter(r => isToday(new Date(r.date))).length;
  const thisWeekReservations = reservations.filter(r => isThisWeek(new Date(r.date))).length;

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Get reservations for a specific date
  const getReservationsForDate = (date: Date) => {
    return reservations.filter(r => isSameDay(new Date(r.date), date));
  };

  // Check if a date is blocked
  const isDateBlocked = (date: Date) => {
    return blockedDates.some(b => isSameDay(new Date(b.block_date), date) && b.is_full_day);
  };

  // Get reservation count for a date
  const getReservationCount = (date: Date) => {
    return reservations.filter(r => isSameDay(new Date(r.date), date)).length;
  };

  // Get dates with reservations for highlighting
  const reservationDates = reservations.map(r => new Date(r.date));
  const blockedDatesList = blockedDates.filter(b => b.is_full_day).map(b => new Date(b.block_date));

  // Selected date reservations
  const selectedDateReservations = selectedDate ? getReservationsForDate(selectedDate) : [];
  const selectedDateBlocked = selectedDate ? blockedDates.find(b => isSameDay(new Date(b.block_date), selectedDate)) : null;

  const handleCreateReservation = () => {
    setSelectedReservation(null);
    setReservationDialogOpen(true);
  };

  const handleEditReservation = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setReservationDialogOpen(true);
  };

  const handleCancelReservation = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setCancelDialogOpen(true);
  };

  const handleReservationSuccess = () => {
    fetchReservations();
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Reservations</h1>
            <p className="text-muted-foreground mt-1">Manage restaurant bookings</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('calendar')}
            >
              <Grid3X3 className="h-4 w-4 mr-2" />
              Calendar
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
            >
              <List className="h-4 w-4 mr-2" />
              Table
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatsCard
            title="Total Reservations"
            value={totalReservations}
            icon={Calendar}
          />
          <StatsCard
            title="Today's Reservations"
            value={todaysReservations}
            icon={CalendarCheck}
          />
          <StatsCard
            title="This Week"
            value={thisWeekReservations}
            icon={CalendarDays}
          />
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className="w-full pointer-events-auto"
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 w-full",
                  month: "space-y-4 w-full",
                  caption: "flex justify-center pt-1 relative items-center",
                  caption_label: "text-sm font-medium",
                  nav: "space-x-1 flex items-center",
                  table: "w-full border-collapse",
                  head_row: "flex w-full",
                  head_cell: "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] text-center",
                  row: "flex w-full mt-2",
                  cell: "relative flex-1 text-center text-sm p-0 focus-within:relative focus-within:z-20",
                  day: cn(
                    "h-12 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-accent rounded-md transition-colors"
                  ),
                  day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                  day_today: "bg-accent text-accent-foreground",
                  day_outside: "text-muted-foreground opacity-50",
                  day_disabled: "text-muted-foreground opacity-50",
                }}
                components={{
                  DayContent: ({ date }) => {
                    const count = getReservationCount(date);
                    const blocked = isDateBlocked(date);
                    return (
                      <div className="flex flex-col items-center justify-center w-full h-full">
                        <span className={cn(blocked && "line-through text-muted-foreground")}>
                          {date.getDate()}
                        </span>
                        <div className="flex gap-0.5 mt-0.5">
                          {blocked && (
                            <div className="w-1.5 h-1.5 rounded-full bg-destructive" />
                          )}
                          {count > 0 && (
                            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          )}
                        </div>
                      </div>
                    );
                  },
                }}
              />
              
              {/* Legend */}
              <div className="flex gap-6 mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                  Has reservations
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="w-2.5 h-2.5 rounded-full bg-destructive" />
                  Blocked
                </div>
              </div>
            </div>

            {/* Selected Date Details */}
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-card-foreground">
                  {selectedDate ? format(selectedDate, 'EEEE, MMMM d, yyyy') : 'Select a date'}
                </h3>
                {selectedDate && !selectedDateBlocked?.is_full_day && (
                  <Button size="sm" onClick={handleCreateReservation}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add
                  </Button>
                )}
              </div>
              
              {selectedDateBlocked && selectedDateBlocked.is_full_day && (
                <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm font-medium text-destructive">Blocked</p>
                  {selectedDateBlocked.reason && (
                    <p className="text-xs text-muted-foreground mt-1">{selectedDateBlocked.reason}</p>
                  )}
                </div>
              )}

              {selectedDateReservations.length === 0 ? (
                <p className="text-sm text-muted-foreground">No reservations for this date</p>
              ) : (
                <div className="space-y-3">
                  {selectedDateReservations.map((reservation) => (
                    <div
                      key={reservation.id}
                      className="p-3 rounded-lg bg-background border border-border"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-foreground">{reservation.name}</span>
                        <Badge
                          variant={reservation.status === 'confirmed' ? 'default' : 'destructive'}
                          className="text-xs"
                        >
                          {reservation.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatTime(reservation.time)} · {reservation.guests} guest{reservation.guests !== 1 ? 's' : ''}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {reservation.email}
                      </div>
                      {reservation.status === 'confirmed' && (
                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleEditReservation(reservation)}
                          >
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-destructive hover:text-destructive"
                            onClick={() => handleCancelReservation(reservation)}
                          >
                            <X className="h-3 w-3 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold text-card-foreground">All Reservations</h3>
              <Button size="sm" onClick={handleCreateReservation}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Name</TableHead>
                  <TableHead className="text-muted-foreground">Email</TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">Time</TableHead>
                  <TableHead className="text-muted-foreground">Guests</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                  <TableHead className="text-muted-foreground">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : reservations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No reservations yet
                    </TableCell>
                  </TableRow>
                ) : (
                  reservations.map((reservation) => (
                    <TableRow key={reservation.id} className="border-border">
                      <TableCell className="font-medium text-card-foreground">
                        {reservation.name}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {reservation.email}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(reservation.date), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatTime(reservation.time)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {reservation.guests}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={reservation.status === 'confirmed' ? 'default' : 'destructive'}
                          className={
                            reservation.status === 'confirmed'
                              ? 'bg-primary text-primary-foreground'
                              : ''
                          }
                        >
                          {reservation.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {reservation.status === 'confirmed' && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditReservation(reservation)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleCancelReservation(reservation)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ReservationDialog
        open={reservationDialogOpen}
        onOpenChange={setReservationDialogOpen}
        reservation={selectedReservation}
        selectedDate={selectedReservation ? new Date(selectedReservation.date) : selectedDate}
        onSuccess={handleReservationSuccess}
      />

      <CancelReservationDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        reservation={selectedReservation}
        onSuccess={handleReservationSuccess}
      />
    </Layout>
  );
};

export default Reservations;
